import { Form } from 'antd';
import { useStepControl } from './useStepControl';
import { StepsResolution886 } from '../../../enums';
import { useHandleActions } from './useHandleActions';
import { useResolution886Report } from '@/context/Resolution886Report';
import type { FormDataValues } from '@/context/Resolution886Report/types';

interface CreateResolution886ReportParams {
	name: string;
	workstation_id: string;
	sector_id: string;
	line_id: string;
	company_id: string;
	organization_id: string;
	review_id: string;
	employer_id: string;
	evaluator_id: string;
	occupational_doctor_id: string;
	safety_and_hygiene_responsible_id: string;
	workers_number: number;
	symptom_location: string;
	early_manifestation: boolean;
	written_work_procedure: boolean;
	training: boolean;
	worker_list_id: string[];
	file_list_id: string[];
}

interface UpdateResolution886ReportParams extends CreateResolution886ReportParams {
	id: string;
	review_id: string;
}

const { useFormInstance } = Form;

export function useGeneralInformation() {
	const form = useFormInstance<FormDataValues>();
	const { handleUpdateStep } = useStepControl();
	const { handleNextStep, handlePreviousStep, newReview, newReviewCreated, report } = useResolution886Report();
	const { handleCreateResolution886Report, handleUpdateResolution886Report, isLoading } = useHandleActions();

	function handleMountPayloadCreate(values: FormDataValues): CreateResolution886ReportParams {
		return {
			...values.general_information,
			review_id: newReview ? undefined : values.general_information.review_id,
			file_list_id: [...values.file_list_id]
		};
	}

	function handleMountPayloadUpdate(values: FormDataValues): UpdateResolution886ReportParams {
		return {
			...values.general_information,
			review_id: newReview && !newReviewCreated ? undefined : values.general_information.review_id,
			file_list_id: [...values.file_list_id],
			id: values.id
		};
	}

	async function checkValidations(): Promise<void> {
		await form.validateFields();
		const values = form.getFieldsValue(['general_information', 'file_list_id', 'id']);

		if (values.id) {
			const payload = handleMountPayloadUpdate(values);
			handleUpdateResolution886Report(payload);
		} else {
			const payload = handleMountPayloadCreate(values);
			handleCreateResolution886Report(payload);
		}
	}

	function handleSetStepBack() {
		if (report.id) {
			handleUpdateStep(StepsResolution886.VIDEO_SELECTION);
		} else {
			handlePreviousStep();
		}
	}

	return {
		checkValidations,
		handleNextStep,
		handlePreviousStep,
		handleCreateResolution886Report,
		handleSetStepBack,
		isLoading
	};
}
